"use client";

import { useState } from "react";
import { Zap } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
 * Rechner „Umsatzverlust durch Ladezeit“ (bleibt laut Auftrag 2026-10-28).
 *
 * Bezugswerte aus Google-Studien (web.dev, Think with Google):
 *   • Bounce-Rate steigt ~32 % wenn Ladezeit 1 → 3 s
 *   • Conversion-Drop ~7 % pro zusätzlicher Sekunde
 * Vereinfachte, ehrliche Heuristiken – der Text darunter nennt die Quelle.
 * ──────────────────────────────────────────────────────────── */
export function SpeedCalculator() {
  const [visitors, setVisitors] = useState(3000);
  const [loadTime, setLoadTime] = useState(5);
  const [aov, setAov] = useState(800); // Ø Auftragswert / Anfrage-Wert

  const baseConv = 2.5; // % bei <2s
  const dropPerSec = 7; // % pro zusätzliche Sekunde
  const secOver2s = Math.max(0, loadTime - 2);
  const effectiveConvPct = Math.max(0.3, baseConv * Math.pow(1 - dropPerSec / 100, secOver2s));
  const possibleConversions = (visitors * baseConv) / 100;
  const actualConversions = (visitors * effectiveConvPct) / 100;
  const lostConversions = Math.max(0, possibleConversions - actualConversions);
  const lostRevenueMonth = Math.round(lostConversions * aov);
  const lostRevenueYear = lostRevenueMonth * 12;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 lg:p-8"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-lg)" }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-rose-400/10 blur-3xl" />

      <div className="relative">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
          style={{ backgroundColor: "var(--fi-error-bg)", color: "var(--fi-error-fg)" }}
        >
          <Zap className="h-3.5 w-3.5" aria-hidden="true" />
          Rechner
        </span>
        <h2 id="rechner" className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
          Wie viel Umsatz verlieren Sie durch eine langsame Website?
        </h2>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          Google-Studien zeigen: <strong style={{ color: "var(--text)" }}>Pro Sekunde Ladezeit über 2 s springen rund 7 % mehr Besucher ab</strong>.
          Schieben Sie die Regler auf Ihre Zahlen – der Rechner zeigt den monatlichen und jährlichen Verlust.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="space-y-6">
            <SliderField id="visitors" label="Besucher pro Monat" value={visitors} min={100} max={50000} step={100} format={(v) => v.toLocaleString("de-DE")} onChange={setVisitors} accent="brand" />
            <SliderField id="loadtime" label="Aktuelle Ladezeit" value={loadTime} min={1} max={12} step={1} format={(v) => `${v} s`} onChange={setLoadTime} accent="error" />
            <SliderField id="aov" label="Ø Auftragswert je Anfrage" value={aov} min={50} max={5000} step={50} format={(v) => `${v.toLocaleString("de-DE")} €`} onChange={setAov} accent="brand" />
            <div className="rounded-xl border p-4 text-xs leading-relaxed" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)", color: "var(--text-tertiary)" }}>
              <p>
                Faustregel: Conversion-Rate rund 2,5 % bei unter 2 s Ladezeit; pro zusätzlicher Sekunde fallen etwa 7 % der
                konvertierenden Besucher ab. Quelle: Google web.dev / Think with Google. Vereinfachtes Modell, kein Versprechen.
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 rounded-xl p-5 lg:p-6" style={{ backgroundColor: "var(--bg-secondary)" }}>
            <ResultRow label="Verlorene Anfragen / Monat" value={Math.round(lostConversions)} suffix="" tone="warning" />
            <ResultRow label="Umsatz-Verlust / Monat" value={lostRevenueMonth} tone="error" suffix=" €" />
            <ResultRow label="Umsatz-Verlust / Jahr" value={lostRevenueYear} tone="error" bold suffix=" €" />

            <div className="mt-3 rounded-lg border-l-4 p-4" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--fi-brand-fg)" }}>
                Unsere Websites laden auf dem Smartphone in
              </p>
              <p className="mt-1 text-2xl font-bold" style={{ color: "var(--fi-brand-fg)" }}>
                unter 2,5 Sekunden
              </p>
              <p className="mt-1 text-xs leading-snug" style={{ color: "var(--fi-brand-fg)" }}>
                Größtes Element sichtbar unter 2,5 s (Core Web Vitals) – serverseitig gerendert, Bilder in modernen Formaten,
                kein fremdes Skript vor der Einwilligung.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
  accent,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
  accent: "brand" | "error";
}) {
  const bg = accent === "brand" ? "var(--fi-brand-bg)" : "var(--fi-error-bg)";
  const fg = accent === "brand" ? "var(--fi-brand-fg)" : "var(--fi-error-fg)";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold" style={{ color: "var(--text)" }}>
          {label}
        </label>
        <span className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums" style={{ backgroundColor: bg, color: fg }}>
          {format(value)}
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="mt-2 w-full accent-brand-500" />
      <div className="mt-1 flex justify-between text-xs" style={{ color: "var(--text-tertiary)" }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

function ResultRow({ label, value, tone, bold, suffix = " €" }: { label: string; value: number; tone: "warning" | "error"; bold?: boolean; suffix?: string }) {
  const toneStyle = tone === "error" ? { color: "var(--fi-error-fg)" } : { color: "var(--fi-warning-fg)" };
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
        {label}
      </p>
      <p className={`tabular-nums ${bold ? "text-2xl font-bold sm:text-3xl" : "text-xl font-semibold"}`} style={toneStyle}>
        {value.toLocaleString("de-DE", { maximumFractionDigits: 0 })}
        {suffix}
      </p>
    </div>
  );
}
